import logger from "../utils/logger";
// src/components/RouteErrorBoundary.jsx
import React, { Component } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, RotateCw, ArrowLeft, LayoutDashboard } from "lucide-react";
import { isChunkLoadError, triggerStaleChunkReload } from "../utils/lazyWithRetry";

class RouteErrorBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunk: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isChunk: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      logger.error("[ROUTE_ERROR_BOUNDARY_CAUGHT]", {
        message: error?.message || String(error),
        stack: error?.stack || null,
        componentStack: errorInfo?.componentStack || null,
        pathname: this.props.location?.pathname || "unknown",
        timestamp: new Date().toISOString(),
        isChunk: isChunkLoadError(error),
      });
    } catch (_) {}

    if (isChunkLoadError(error)) {
      triggerStaleChunkReload(error);
    }
  }

  componentDidUpdate(prevProps) {
    // Automatically reset error state when navigation occurs (Back/Forward or link click)
    if (
      this.state.hasError &&
      (prevProps.location?.pathname !== this.props.location?.pathname ||
        prevProps.location?.key !== this.props.location?.key)
    ) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isChunk: false,
      });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunk: false,
    });
  };

  handleReload = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  handleGoDashboard = () => {
    this.handleReset();
    if (this.props.navigate) {
      this.props.navigate("/workspace");
    } else if (typeof window !== "undefined") {
      window.location.href = "/workspace";
    }
  };

  render() {
    if (this.state.hasError) {
      const isChunk = this.state.isChunk;
      return (
        <div className="flex-1 w-full min-h-[350px] flex items-center justify-center p-6 bg-slate-50/60 antialiased text-slate-800 select-none">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8 space-y-5 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shadow-sm">
              <AlertTriangle className="h-7 w-7 stroke-[2.2]" />
            </div>

            <div className="space-y-1.5">
              <h3 className="text-lg font-black text-slate-900 tracking-tight">
                {isChunk ? "Application Update Detected" : "Unable to Display Section"}
              </h3>
              <p className="text-xs font-semibold text-slate-500 max-w-sm mx-auto leading-relaxed">
                {isChunk
                  ? "A newer version was deployed. Please reload to load the latest components."
                  : "An unexpected error occurred while rendering this module. Your other tabs and navigation remain active."}
              </p>
            </div>

            {this.state.error && !isChunk && (
              <div className="bg-slate-900/95 rounded-xl p-3 text-left overflow-hidden">
                <p className="font-mono text-[11px] text-rose-300 break-words line-clamp-3">
                  {this.state.error?.message || String(this.state.error)}
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-center gap-2.5 pt-2">
              <button
                type="button"
                onClick={this.handleReset}
                className="px-4 py-2 bg-[#033F99] hover:bg-blue-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 flex items-center gap-1.5 cursor-pointer"
              >
                <RotateCw className="h-3.5 w-3.5" />
                Try Again
              </button>

              <button
                type="button"
                onClick={this.handleReload}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all border border-slate-200 active:scale-95 flex items-center gap-1.5 cursor-pointer"
              >
                Reload Page
              </button>

              <button
                type="button"
                onClick={this.handleGoDashboard}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all border border-slate-200 active:scale-95 flex items-center gap-1.5 cursor-pointer"
              >
                <LayoutDashboard className="h-3.5 w-3.5" />
                Workspace
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function RouteErrorBoundary({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <RouteErrorBoundaryInner location={location} navigate={navigate}>
      {children}
    </RouteErrorBoundaryInner>
  );
}

export default RouteErrorBoundary;
